import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import {
  LayoutDashboard, Users, Package, Receipt, FileSpreadsheet, Clock,
  BarChart3, Plug, Settings as SettingsIcon, LogOut, Upload, Store, CreditCard, Globe,
} from "lucide-react";
import { isEmbedded } from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/consignors", label: "Consignors", icon: Users, testid: "nav-consignors" },
  { to: "/items", label: "Items", icon: Package, testid: "nav-items" },
  { to: "/sales", label: "Sales", icon: Receipt, testid: "nav-sales" },
  { to: "/settlements", label: "Settlements", icon: FileSpreadsheet, testid: "nav-settlements" },
  { to: "/expiring", label: "Expiring Items", icon: Clock, testid: "nav-expiring-items" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/import", label: "Import CSV", icon: Upload, testid: "nav-import" },
  { to: "/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { store } = useStore();
  const navigate = useNavigate();
  const nav = user?.is_platform_admin
    ? [...NAV, { to: "/traffic", label: "Traffic", icon: Globe, testid: "nav-traffic" }] : NAV;

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-[#faf9f5]">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-slate-900 text-slate-300 border-r border-slate-800 fixed h-full z-20">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-800/80">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Store className="w-5 h-5 text-slate-950" />
          </div>
          <div>
            <p className="font-display font-extrabold text-white leading-tight">ConsignOut</p>
            <p className="text-[11px] text-slate-500 truncate max-w-[140px]">{store?.name || "—"}</p>
          </div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-6 py-2.5 text-sm transition-colors",
                isActive
                  ? "bg-emerald-900/40 text-emerald-300 font-semibold border-r-4 border-emerald-400"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
              )}>
              <n.icon className="w-[18px] h-[18px]" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800/80 p-4">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">
              {(user?.name || "?").charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          {!isEmbedded() && (
            <button data-testid="logout-btn" onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-slate-900 text-white flex items-center gap-2 px-4 py-3 overflow-x-auto">
        <Store className="w-5 h-5 text-emerald-400 shrink-0" />
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`m-${n.testid}`}
            className={({ isActive }) => cn("text-xs whitespace-nowrap px-2 py-1 rounded",
              isActive ? "bg-emerald-500 text-slate-950 font-semibold" : "text-slate-300")}>
            {n.label}
          </NavLink>
        ))}
      </div>

      <main className="flex-1 min-w-0 md:ml-64 pt-16 md:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
